import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  Icon,
  Input,
  Stack,
  Tag,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { DynamicArtifactTemplate } from '@eluve/llm-outputs';
import {
  useTenantIdFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { templateBuilderStore, useUploadUserFile } from '@eluve/smart-blocks';

const buildGenerateTemplateFormSchema = (requiredMessage: string) =>
  z.object({
    documentDescription: z
      .string()
      .min(1, requiredMessage)
      .describe(
        'Description of the desired medical document format, including sections, fields, and structure requirements',
      ),
    documentType: z
      .string()
      .optional()
      .describe(
        'Type of medical document (e.g., "clinical note", "lab report", "discharge summary", "patient assessment")',
      ),
    requiredFields: z
      .string()
      .optional()
      .describe('Comma-separated list of required fields'),
    constraints: z
      .string()
      .optional()
      .describe(
        'Any specific constraints, validation rules, or formatting requirements',
      ),
    pdfFile: z.instanceof(File).optional(),
  });

type GenerateTemplateFormValues = z.infer<
  ReturnType<typeof buildGenerateTemplateFormSchema>
>;

interface GenerateOutputTemplateProps {
  onTemplateGenerated: (template: DynamicArtifactTemplate) => void;
}

export const GenerateOutputTemplate: FC<GenerateOutputTemplateProps> = ({
  onTemplateGenerated,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [uploadedPdfId, setUploadedPdfId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiClient = useApiClient();
  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromSession();

  const { uploadUserFileToGCS } = useUploadUserFile(tenantId!);

  const generateTemplateFormSchema = useMemo(
    () =>
      buildGenerateTemplateFormSchema(
        t('outputTemplates.generateDialog.documentDescriptionRequired'),
      ),
    [t],
  );

  const form = useForm<GenerateTemplateFormValues>({
    resolver: zodResolver(generateTemplateFormSchema),
    defaultValues: {
      documentDescription: '',
      documentType: '',
      requiredFields: '',
      constraints: '',
      pdfFile: undefined,
    },
    mode: 'onChange',
  });

  const documentDescription = form.watch('documentDescription');
  const isValid = Boolean(documentDescription?.trim());

  const handlePdfFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('outputTemplates.generateDialog.onlyPdfAllowed'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('outputTemplates.generateDialog.fileTooLarge'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setIsUploadingPdf(true);
      const extension = file.name.split('.').pop();
      if (!extension) {
        toast.error(t('outputTemplates.generateDialog.invalidExtension'));
        return;
      }

      const userUploadId = await uploadUserFileToGCS({
        file,
        mimeType: 'application/pdf',
        extension,
        fileType: 'CHAT_DOCUMENT',
      });

      if (!userUploadId) {
        toast.error(t('outputTemplates.generateDialog.uploadPdfFailed'));
        return;
      }

      setUploadedPdfId(userUploadId);
      form.setValue('pdfFile', file);
      toast.success(t('outputTemplates.generateDialog.pdfUploadSuccess'));
    } catch {
      toast.error(t('outputTemplates.generateDialog.uploadPdfFailed'));
    } finally {
      setIsUploadingPdf(false);
    }
  };

  const handleRemovePdf = () => {
    setUploadedPdfId(null);
    form.setValue('pdfFile', undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (values: GenerateTemplateFormValues) => {
    setIsSubmitting(true);

    try {
      let presignedUrl: string | undefined = undefined;

      // Generate presigned URL if there's an uploaded file
      if (uploadedPdfId) {
        if (userId) {
          const presignedResponse = await apiClient.users.getUserFileSignedUrl({
            params: {
              tenantId: tenantId!,
              userId,
              fileId: uploadedPdfId,
            },
          });

          if (
            presignedResponse.status === 200 &&
            presignedResponse.body.presignedUrl
          ) {
            presignedUrl = presignedResponse.body.presignedUrl;
          }
        }
      }

      // Parse comma-separated required fields into array
      const requiredFieldsArray = values.requiredFields
        ?.split(',')
        .map((field) => field.trim())
        .filter(Boolean);

      const response = await apiClient.adminLlm.createOutputTemplate({
        body: {
          documentDescription: values.documentDescription,
          documentType: values.documentType ?? undefined,
          requiredFields: requiredFieldsArray?.length
            ? requiredFieldsArray
            : undefined,
          constraints: values.constraints ?? undefined,
          presignedUrl: presignedUrl,
        },
      });

      if (response.status === 201) {
        const { dynamicArtifact, hydrateJson } = response.body;
        onTemplateGenerated(dynamicArtifact);
        templateBuilderStore.send(hydrateJson);

        toast.success(t('outputTemplates.generateDialog.generatedSuccess'));

        // Reset form
        form.reset();
      } else if (response.status === 422) {
        const errorMessage =
          response.body.message ??
          t('outputTemplates.generateDialog.generateFailed');
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t('outputTemplates.generateDialog.generateOutputFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap={4}>
      <Heading size="2xl">{t('outputTemplates.generateDialog.title')}</Heading>
      <Text weight="regular" color="secondary">
        {t('outputTemplates.generateDialog.description')}
      </Text>

      <Form {...form}>
        <form
          className="flex w-full flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="documentDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text color="tertiary">
                    {t(
                      'outputTemplates.generateDialog.documentDescriptionLabel',
                    )}{' '}
                    <span className="text-text-negative-subtle">
                      {t('outputTemplates.generateDialog.requiredIndicator')}
                    </span>
                  </Text>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t(
                      'outputTemplates.generateDialog.documentDescriptionPlaceholder',
                    )}
                    disabled={isSubmitting}
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="documentType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text color="tertiary">
                    {t('outputTemplates.generateDialog.typeLabel')}
                  </Text>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      'outputTemplates.generateDialog.typePlaceholder',
                    )}
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="requiredFields"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text color="tertiary">
                    {t('outputTemplates.generateDialog.requiredFieldsLabel')}
                  </Text>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      'outputTemplates.generateDialog.requiredFieldsPlaceholder',
                    )}
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="constraints"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text color="tertiary">
                    {t('outputTemplates.generateDialog.constraintsLabel')}
                  </Text>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t(
                      'outputTemplates.generateDialog.constraintsPlaceholder',
                    )}
                    disabled={isSubmitting}
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pdfFile"
            render={() => (
              <FormItem>
                <FormLabel>
                  <Text color="tertiary">
                    {t('outputTemplates.generateDialog.exampleDocumentLabel')}
                    {'  '}
                    <Tag
                      size="xs"
                      color="teal"
                      label={t('outputTemplates.generateDialog.beta')}
                    />
                  </Text>
                </FormLabel>
                <FormControl>
                  <Stack gap={2} align="stretch">
                    {uploadedPdfId ? (
                      <Stack
                        className="rounded-lg border border-border-primary bg-bg-primary p-3"
                        gap={3}
                        justify="between"
                        direction="horizontal"
                      >
                        <Stack gap={2} direction="horizontal">
                          <Text inline color="tertiary">
                            <span
                              role="img"
                              aria-label={t(
                                'outputTemplates.generateDialog.pdfDocumentAria',
                              )}
                            >
                              📄
                            </span>
                            {t('outputTemplates.generateDialog.pdfUploaded')}
                          </Text>
                        </Stack>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isSubmitting || isUploadingPdf}
                          onClick={handleRemovePdf}
                        >
                          <Icon name="Trash" size="auto" />
                        </Button>
                      </Stack>
                    ) : (
                      <Input
                        ref={fileInputRef}
                        accept=".pdf"
                        className="rounded-lg border border-border-primary bg-bg-primary"
                        disabled={isSubmitting || isUploadingPdf}
                        onChange={handlePdfFileChange}
                        type="file"
                      />
                    )}
                    {isUploadingPdf && (
                      <Text size="xs" color="secondary">
                        {t('outputTemplates.generateDialog.uploadingPdf')}
                      </Text>
                    )}
                  </Stack>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button disabled={!isValid} loading={isSubmitting} type="submit">
            {t('outputTemplates.generateDialog.generateTemplate')}
          </Button>
        </form>
      </Form>
    </Stack>
  );
};
